import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";

import { useTranslation } from "react-i18next";

import { useFormat } from "hooks/useFormat";
import { HandlerType } from "interfaces/handlerType";
import { ILog } from "interfaces/logs.interface";
import {
  INoPresenceConditions,
  IRuleAwakeConditions,
  IRuleDeviceOfflineConditions,
  IRuleMovementConditions,
  ISoundLevelConditions,
} from "interfaces/rules.interface";
import { RuleTypes } from "interfaces/ruleType";

import { InfoField } from "../components/InfoField";
import { LogPresetAwake } from "../components/LogPresetAwake";
import { LogPresetDeviceOffline } from "../components/LogPresetDeviceOffline";
import { LogPresetMovement } from "../components/LogPresetMovement";
import { LogPresetNoPresence } from "../components/LogPresetNoPresence";
import { LogPresetSoundLevel } from "../components/LogPresetSoundLevel";

const handlerNameMap: { [key: string]: HandlerType } = {
  "notification-handler.skyresponse": "skyresponse",
  "notification-handler.netnordic": "netnordic",
  "notification-handler.sensio": "sensio",
  "notification-handler.sms": "sms",
  "notification-handler.webhook": "webhook",
  "notification-handler.email": "email",
};

type NotificationLogDetailsDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  notificationLog?: ILog;
  zone: string;
};

export function NotificationLogDetailsDialog({
  isOpen,
  onClose,
  notificationLog,
  zone,
}: NotificationLogDetailsDialogProps) {
  const { t } = useTranslation();
  const { format } = useFormat();

  if (!notificationLog?.rule) return null;

  return (
    <Dialog open={isOpen} onClose={onClose}>
      <DialogTitle>{t("notification_log_details")}</DialogTitle>
      <DialogContent
        dividers
        sx={{ display: "flex", flexDirection: "column", gap: 2 }}
      >
        <InfoField
          label={
            notificationLog.rule.type === RuleTypes.DeviceOffline
              ? t("Zone")
              : t("subject")
          }
          value={notificationLog?.subject_identifier || zone}
        />
        <InfoField label={t("Type")} value={t(notificationLog.rule.type)} />
        {notificationLog.rule.current_state?.state && (
          <InfoField
            label={t("Current state")}
            value={t(notificationLog.rule.current_state.state)}
          />
        )}
        {notificationLog.rule.type === RuleTypes.Awake && (
          <LogPresetAwake
            preset={notificationLog.rule.conditions as IRuleAwakeConditions}
            state={notificationLog.rule?.current_state?.state || ""}
          />
        )}
        {notificationLog.rule.type === RuleTypes.SoundLevel && (
          <LogPresetSoundLevel
            preset={notificationLog.rule.conditions as ISoundLevelConditions}
          />
        )}
        {notificationLog.rule.type === RuleTypes.Movement && (
          <LogPresetMovement
            preset={notificationLog.rule.conditions as IRuleMovementConditions}
          />
        )}
        {notificationLog.rule.type === RuleTypes.NoPresence && (
          <LogPresetNoPresence
            preset={notificationLog.rule.conditions as INoPresenceConditions}
            state={notificationLog.rule?.current_state?.state || ""}
          />
        )}
        {notificationLog.rule.type === RuleTypes.DeviceOffline && (
          <LogPresetDeviceOffline
            preset={
              notificationLog.rule.conditions as IRuleDeviceOfflineConditions
            }
          />
        )}
        <InfoField
          label={t("timestamp")}
          value={
            notificationLog.rule.current_state?.timestamp
              ? format(
                  new Date(notificationLog.rule.current_state.timestamp),
                  "PPpp",
                )
              : ""
          }
        />
        <InfoField
          label={t("notification_message")}
          value={notificationLog.rule?.current_state?.detail}
        />
        <Box>
          <Typography sx={{ marginBottom: 2 }}>
            {t("notification_sent_through_below_handlers")}
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {notificationLog.actions?.length ? (
              notificationLog.actions.map((item) => (
                <Chip
                  key={item.type_id}
                  label={
                    item.type in handlerNameMap
                      ? t(handlerNameMap[item.type])
                      : ""
                  }
                />
              ))
            ) : (
              <Typography>
                {t("no_handler_is_active_at_the_time_the_notification_is_sent")}
              </Typography>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("close")}</Button>
      </DialogActions>
    </Dialog>
  );
}
